import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Select, Table, TextInput, Grid, GridCol } from '@mantine/core';
import ModalComponent from '../../../components/modal';
import PaginationComponent from '../../../components/pagination';
import { LoginState } from '../../../client-redux/login/reducer';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { entityTypes } from '../../utils';
import { fetchUsers, IUser, IUserResponse } from '../../../../libraries/user';
import { IEntity } from '../../../../libraries/entity';
import './styles.scss';

interface ModalProps {
  isOpen: boolean;
  hideBtn: () => void;
  lang: Language;
  chooseUser: (user: IUser) => void;
}

const ChooseUserModal = ({ isOpen, hideBtn, lang, chooseUser }: ModalProps) => {
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);

  const [entid, setEntid] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [uid] = useState<number>();
  const [, setId] = useState<number>();
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [message, setMessage] = useState<IUserResponse>();
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [, setLastSelectedPage] = useState<number>(1);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const getUsersList = () => {
    if (!loginInfo.token) {
      return;
    }
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;
    fetchUsers(controller, entid, limit, offset, shouldFetchTotalRows, name, uid)
      .then(response => {
        if (response.statusOper.code === 0) {
          setMessage(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
          setLoading(false);
        } else {
          setNoResults(true);
          setLoading(false);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    } else {
      getUsersList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, currentPage]);

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
    setLastSelectedPage(page);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(0);
    setOffset(0);
    setCurrentPage(1);
  };

  /* Entity handle*/
  const handleEntitySelect = (value: number | 0) => {
    if (value !== null) {
      const selectedEntid = Number(value);
      setEntid(selectedEntid);
    }
  };

  /* Name handle*/
  const handleTableByName = (value: string) => {
    if (value !== null) {
      const selectedName = String(value);
      setName(selectedName);
    }
  };

  /* ID  handle*/
  const handleTableByID = (value: number) => {
    if (value !== null) {
      const selectedID = Number(value);
      setId(selectedID);
    }
  };

  const handleEntitySelection = (user: IUser) => {
    chooseUser(user);
    hideBtn();
  };

  //Allow for enter key to search
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      getUsersList();
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={lang.HELPDESK_CHOOSE_ENTITY}
        classname="choose-user-modal"
        content={
          <Modal.Body style={{ margin: '0', padding: '0' }}>
            <Grid classNames={{ root: 'choose-distribuition-user' }}>
              <GridCol span={6}>
                {/* Entity */}
                <Select
                  size="sm"
                  withCheckIcon={true}
                  label={lang.GLOBAL_ENTITY}
                  data={[
                    ...(entities
                      ? entities.map(entity => ({ value: entity.entid.toString(), label: entity.name }))
                      : [])
                  ]}
                  value={entid.toString()}
                  onChange={value => handleEntitySelect(Number(value))}
                />
              </GridCol>

              <GridCol span={6}>
                {/* Name */}
                <TextInput
                  label={lang.GLOBAL_NAME}
                  onChange={event => handleTableByName(String(event.target.value))}
                  onKeyDown={handleKeyDown}
                />
              </GridCol>

              <GridCol span={6}>
                {/* ID */}
                <Select
                  label={lang.USER_UID}
                  data={entityTypes(lang)}
                  placeholder={lang.GLOBAL_ALL}
                  onChange={value => handleTableByID(Number(value))}
                />
              </GridCol>

              {/* Search */}
              <Button radius="md" onClick={getUsersList}>
                {lang.GLOBAL_SEARCH}
              </Button>

              {/* Entity List */}
              {message !== undefined && (
                <>
                  <GridCol span={12}>
                    <Table.ScrollContainer minWidth={500}>
                      <Table className={'distribuition-table'} striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Entid</Table.Th>
                            <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                          {noResults ? (
                            <Table.Tr>
                              <Table.Td colSpan={16} style={{ textAlign: 'center', width: '1100px' }}>
                                {lang.GLOBAL_NO_RESULTS}
                              </Table.Td>
                            </Table.Tr>
                          ) : (
                            message?.users.map((user, index) => {
                              return (
                                <Table.Tr
                                  key={index}
                                  onClick={() => handleEntitySelection(user)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <Table.Td>{user.entid}</Table.Td>
                                  <Table.Td>{user.name}</Table.Td>
                                </Table.Tr>
                              );
                            })
                          )}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </GridCol>
                  <PaginationComponent
                    total={totalRowsResponse}
                    statusOperCode={statusOperCode}
                    itemsPerPage={limit}
                    dynamicLimit={limit}
                    onPageChange={changePage}
                    onResultsChange={resultsSelect}
                  />
                </>
              )}
            </Grid>
          </Modal.Body>
        }
      />
    </>
  );
};

export default ChooseUserModal;
